import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaEdit,
  FaEnvelope,
  FaKey,
  FaMapMarkerAlt,
  FaPause,
  FaPlay,
  FaSearch,
  FaSpinner,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  deletePartenaireKata,
  findOwnedPartenairesKata,
  managePartenaireKataLifecycle,
  verifyPartenaireKata,
} from "../../../services/partenairesKataApi";

import {
  formatDate,
  getDepartmentLabel,
} from "../utils/kataUtils";

import KataButton from "../ui/KataButton";
import ModalShell from "../ui/ModalShell";

const EMPTY_CREDENTIALS = {
  email: "",
  password: "",
};

const STATUS_CONFIG = {
  active: {
    label: "Annonce active",
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300",
  },

  paused: {
    label: "Annonce en pause",
    className:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-300",
  },

  matched: {
    label: "Partenaire trouvé",
    className:
      "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-400/20 dark:bg-blue-400/10 dark:text-blue-300",
  },

  expired: {
    label: "Annonce expirée",
    className:
      "border-red-200 bg-red-50 text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-300",
  },
};

const getEffectiveStatus = (partenaire) => {
  if (!partenaire) {
    return "active";
  }

  const today = new Date().toISOString().slice(0, 10);

  if (
    partenaire.status === "active" &&
    partenaire.expires_at &&
    partenaire.expires_at < today
  ) {
    return "expired";
  }

  return partenaire.status || "active";
};

const getStatusConfig = (partenaire) => {
  const status = getEffectiveStatus(partenaire);

  return (
    STATUS_CONFIG[status] || {
      label: status,
      className:
        "border-slate-200 bg-slate-100 text-slate-700 dark:border-white/10 dark:bg-white/10 dark:text-slate-300",
    }
  );
};

export default function ManageAnnonceModal({
  partenaire,
  isOpen,
  onClose,
  onEdit,
  onChanged,
}) {
  const [credentials, setCredentials] = useState(
    EMPTY_CREDENTIALS
  );

  const [ownedPartenaires, setOwnedPartenaires] =
    useState([]);

  const [selectedPartenaire, setSelectedPartenaire] =
    useState(null);

  const [authenticationError, setAuthenticationError] =
    useState("");

  const [isAuthenticating, setIsAuthenticating] =
    useState(false);

  const [pendingAction, setPendingAction] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setCredentials(EMPTY_CREDENTIALS);
      setOwnedPartenaires([]);
      setSelectedPartenaire(null);
      setAuthenticationError("");
      setIsAuthenticating(false);
      setPendingAction("");

      return;
    }

    setCredentials(EMPTY_CREDENTIALS);
    setOwnedPartenaires([]);
    setSelectedPartenaire(null);
    setAuthenticationError("");
  }, [isOpen, partenaire?.id]);

  const effectiveStatus = useMemo(
    () => getEffectiveStatus(selectedPartenaire),
    [selectedPartenaire]
  );

  const handleCredentialChange = (event) => {
    const { name, value } = event.target;

    setCredentials((current) => ({
      ...current,
      [name]: value,
    }));

    setAuthenticationError("");
  };

  const handleAuthentication = async (event) => {
    event.preventDefault();

    const normalizedCredentials = {
      email: credentials.email.trim().toLowerCase(),
      password: credentials.password,
    };

    if (
      !normalizedCredentials.email ||
      !normalizedCredentials.password
    ) {
      setAuthenticationError(
        "Veuillez renseigner votre email et votre mot de passe."
      );

      return;
    }

    setIsAuthenticating(true);
    setAuthenticationError("");

    try {
      /*
       * Si la modale a été ouverte depuis une carte,
       * nous vérifions directement cette annonce.
       */
      if (partenaire?.id) {
        const response = await verifyPartenaireKata(
          partenaire.id,
          normalizedCredentials
        );

        setCredentials(normalizedCredentials);
        setOwnedPartenaires([response.data]);
        setSelectedPartenaire(response.data);

        toast.success(response.message || "Annonce vérifiée.");

        return;
      }

      /*
       * Si la modale a été ouverte depuis le bouton global
       * « Gérer mon annonce », nous recherchons toutes les annonces.
       */
      const response = await findOwnedPartenairesKata(
        normalizedCredentials
      );

      const annonces = response.data || [];

      setCredentials(normalizedCredentials);
      setOwnedPartenaires(annonces);

      if (annonces.length === 1) {
        setSelectedPartenaire(annonces[0]);
      }

      toast.success(response.message);
    } catch (error) {
      setAuthenticationError(
        error.message ||
          "Adresse email ou mot de passe incorrect."
      );
    } finally {
      setIsAuthenticating(false);
    }
  };

  const replaceUpdatedPartenaire = (updatedPartenaire) => {
    setOwnedPartenaires((current) =>
      current.map((item) =>
        item.id === updatedPartenaire.id
          ? updatedPartenaire
          : item
      )
    );

    setSelectedPartenaire(updatedPartenaire);
  };

  const handleLifecycleAction = async (action) => {
    if (!selectedPartenaire) {
      return;
    }

    if (
      action === "matched" &&
      !window.confirm(
        "Confirmez-vous avoir trouvé un partenaire ? L'annonce ne sera plus affichée publiquement."
      )
    ) {
      return;
    }

    setPendingAction(action);

    try {
      const response =
        await managePartenaireKataLifecycle(
          selectedPartenaire.id,
          credentials,
          action
        );

      replaceUpdatedPartenaire(response.data);

      toast.success(response.message);

      if (onChanged) {
        onChanged();
      }
    } catch (error) {
      toast.error(
        error.message ||
          "Impossible de modifier le statut de l'annonce."
      );
    } finally {
      setPendingAction("");
    }
  };

  const handleDelete = async () => {
    if (!selectedPartenaire) {
      return;
    }

    const confirmed = window.confirm(
      "Voulez-vous réellement supprimer cette annonce ? Cette action est définitive."
    );

    if (!confirmed) {
      return;
    }

    setPendingAction("delete");

    try {
      const response = await deletePartenaireKata(
        selectedPartenaire.id,
        credentials
      );

      const remainingPartenaires =
        ownedPartenaires.filter(
          (item) => item.id !== selectedPartenaire.id
        );

      setOwnedPartenaires(remainingPartenaires);
      setSelectedPartenaire(
        remainingPartenaires.length === 1
          ? remainingPartenaires[0]
          : null
      );

      toast.success(response.message);

      if (onChanged) {
        onChanged();
      }

      if (remainingPartenaires.length === 0) {
        onClose();
      }
    } catch (error) {
      toast.error(
        error.message ||
          "Impossible de supprimer l'annonce."
      );
    } finally {
      setPendingAction("");
    }
  };

  const handleEdit = () => {
    if (!selectedPartenaire || !onEdit) {
      return;
    }

    onEdit({
      partenaire: selectedPartenaire,
      credentials,
    });
  };

  const renderAuthenticationForm = () => (
    <form
      onSubmit={handleAuthentication}
      className="space-y-5"
    >
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm leading-6 text-blue-800 dark:border-blue-400/20 dark:bg-blue-400/10 dark:text-blue-200">
        Utilisez l’adresse email et le mot de passe choisis
        lors de la publication. Vous pourrez également
        retrouver une annonce expirée ou mise en pause.
      </div>

      <div>
        <label
          htmlFor="kata-management-email"
          className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
        >
          Adresse email
        </label>

        <div className="relative">
          <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            id="kata-management-email"
            type="email"
            name="email"
            value={credentials.email}
            onChange={handleCredentialChange}
            autoComplete="email"
            placeholder="votre@email.fr"
            className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-400/10 dark:border-white/10 dark:bg-white/5 dark:text-white"
            required
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="kata-management-password"
          className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
        >
          Mot de passe
        </label>

        <div className="relative">
          <FaKey className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            id="kata-management-password"
            type="password"
            name="password"
            value={credentials.password}
            onChange={handleCredentialChange}
            autoComplete="current-password"
            placeholder="Votre mot de passe"
            className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-400/10 dark:border-white/10 dark:bg-white/5 dark:text-white"
            required
          />
        </div>
      </div>

      {authenticationError ? (
        <div
          role="alert"
          className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-300"
        >
          {authenticationError}
        </div>
      ) : null}

      <KataButton
        type="submit"
        icon={isAuthenticating ? FaSpinner : FaSearch}
        disabled={isAuthenticating}
        className={
          isAuthenticating
            ? "w-full [&_svg]:animate-spin"
            : "w-full"
        }
      >
        {isAuthenticating
          ? "Vérification en cours..."
          : partenaire?.id
            ? "Accéder à cette annonce"
            : "Retrouver mes annonces"}
      </KataButton>
    </form>
  );

  const renderOwnedPartenaires = () => (
    <div className="space-y-4">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300">
        {ownedPartenaires.length} annonce
        {ownedPartenaires.length > 1 ? "s" : ""} retrouvée
        {ownedPartenaires.length > 1 ? "s" : ""}.
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {ownedPartenaires.map((item) => {
          const statusConfig = getStatusConfig(item);

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedPartenaire(item)}
              className="rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-orange-300 hover:shadow-lg dark:border-white/10 dark:bg-white/5"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-semibold text-slate-950 dark:text-white">
                    {item.prenom} {item.nom}
                  </p>

                  <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    {item.club}
                  </p>
                </div>

                <span
                  className={[
                    "rounded-full border px-3 py-1 text-xs font-semibold",
                    statusConfig.className,
                  ].join(" ")}
                >
                  {statusConfig.label}
                </span>
              </div>

              <div className="mt-4 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <FaMapMarkerAlt className="text-orange-500" />

                {getDepartmentLabel(item.departement)}
              </div>

              <p className="mt-3 text-sm font-bold text-slate-700 dark:text-slate-200">
                {item.passage_grade}
                {item.kata ? ` · ${item.kata}` : ""}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderSelectedPartenaire = () => {
    if (!selectedPartenaire) {
      return null;
    }

    const statusConfig = getStatusConfig(
      selectedPartenaire
    );

    return (
      <div className="space-y-6">
        {ownedPartenaires.length > 1 ? (
          <button
            type="button"
            onClick={() => setSelectedPartenaire(null)}
            className="text-sm font-semibold text-orange-600 transition hover:text-orange-700"
          >
            ← Retour à mes annonces
          </button>
        ) : null}

        <section className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
            <div>
              <p className="text-2xl font-semibold text-slate-950 dark:text-white">
                {selectedPartenaire.prenom}{" "}
                {selectedPartenaire.nom}
              </p>

              <p className="mt-1 font-semibold text-slate-500 dark:text-slate-400">
                {selectedPartenaire.club}
              </p>
            </div>

            <span
              className={[
                "w-fit rounded-full border px-4 py-2 text-xs font-semibold",
                statusConfig.className,
              ].join(" ")}
            >
              {statusConfig.label}
            </span>
          </div>

          <div className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
            <div className="rounded-2xl bg-white p-4 dark:bg-slate-900">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                Localisation
              </p>

              <p className="mt-1 font-semibold text-slate-800 dark:text-white">
                {getDepartmentLabel(
                  selectedPartenaire.departement
                )}
              </p>
            </div>

            <div className="rounded-2xl bg-white p-4 dark:bg-slate-900">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                Grade préparé
              </p>

              <p className="mt-1 font-semibold text-slate-800 dark:text-white">
                {selectedPartenaire.passage_grade}
              </p>
            </div>

            <div className="rounded-2xl bg-white p-4 dark:bg-slate-900">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                Kata
              </p>

              <p className="mt-1 font-semibold text-slate-800 dark:text-white">
                {selectedPartenaire.kata ||
                  "Non renseigné"}
              </p>
            </div>

            <div className="rounded-2xl bg-white p-4 dark:bg-slate-900">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                Expiration
              </p>

              <p className="mt-1 font-semibold text-slate-800 dark:text-white">
                {selectedPartenaire.expires_at
                  ? formatDate(
                      selectedPartenaire.expires_at
                    )
                  : "Non définie"}
              </p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-slate-950 dark:text-white">
            Actions disponibles
          </h3>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <KataButton
              icon={FaEdit}
              variant="secondary"
              onClick={handleEdit}
              disabled={Boolean(pendingAction)}
              className="w-full"
            >
              Modifier l’annonce
            </KataButton>

            {effectiveStatus === "active" ? (
              <KataButton
                icon={
                  pendingAction === "pause"
                    ? FaSpinner
                    : FaPause
                }
                variant="secondary"
                onClick={() =>
                  handleLifecycleAction("pause")
                }
                disabled={Boolean(pendingAction)}
                className={
                  pendingAction === "pause"
                    ? "w-full [&_svg]:animate-spin"
                    : "w-full"
                }
              >
                Mettre en pause
              </KataButton>
            ) : null}

            {["paused", "matched"].includes(
              effectiveStatus
            ) ? (
              <KataButton
                icon={
                  pendingAction === "reactivate"
                    ? FaSpinner
                    : FaPlay
                }
                variant="secondary"
                onClick={() =>
                  handleLifecycleAction("reactivate")
                }
                disabled={Boolean(pendingAction)}
                className={
                  pendingAction === "reactivate"
                    ? "w-full [&_svg]:animate-spin"
                    : "w-full"
                }
              >
                Réactiver l’annonce
              </KataButton>
            ) : null}

            {effectiveStatus === "active" ? (
              <KataButton
                icon={
                  pendingAction === "matched"
                    ? FaSpinner
                    : FaCheckCircle
                }
                variant="secondary"
                onClick={() =>
                  handleLifecycleAction("matched")
                }
                disabled={Boolean(pendingAction)}
                className={
                  pendingAction === "matched"
                    ? "w-full [&_svg]:animate-spin"
                    : "w-full"
                }
              >
                J’ai trouvé un partenaire
              </KataButton>
            ) : null}

            <KataButton
              icon={
                pendingAction === "renew"
                  ? FaSpinner
                  : FaSyncAlt
              }
              onClick={() =>
                handleLifecycleAction("renew")
              }
              disabled={Boolean(pendingAction)}
              className={
                pendingAction === "renew"
                  ? "w-full [&_svg]:animate-spin"
                  : "w-full"
              }
            >
              Renouveler pour six mois
            </KataButton>

            <KataButton
              icon={
                pendingAction === "delete"
                  ? FaSpinner
                  : FaTrash
              }
              variant="danger"
              onClick={handleDelete}
              disabled={Boolean(pendingAction)}
              className={
                pendingAction === "delete"
                  ? "w-full [&_svg]:animate-spin sm:col-span-2"
                  : "w-full sm:col-span-2"
              }
            >
              Supprimer définitivement
            </KataButton>
          </div>
        </section>
      </div>
    );
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Gérer mes annonces"
      subtitle="Modifiez, renouvelez ou clôturez votre recherche de partenaire."
      maxWidth="max-w-3xl"
    >
      <div className="p-5 sm:p-7">
        {ownedPartenaires.length === 0
          ? renderAuthenticationForm()
          : selectedPartenaire
            ? renderSelectedPartenaire()
            : renderOwnedPartenaires()}
      </div>
    </ModalShell>
  );
}